import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { message } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'

import { setConfig, setShowBuyModal, setSelectedTag, setSelectedDocument } from '../../../redux/actions/actions'
import ModalBuy from '../GenerateCourse/Modals/ModalBuy/ModalBuy'
import requests from '../../axios/requests'
import variableLanguage from '../../utils/hooks/hookGetVariablesLanguage'

import useTrajectory from './gen/useTrajectory'
import { ChipsBox, PlanChip, SkillAdd, StepsNav, stepSummary } from './gen/GenFrame'
import SalaryPanel from './gen/SalaryPanel'
import DocSource from './gen/DocSource'
import { plural, pluralN, skillStatus } from './gen/genUtils'
import s from './gen/gen.module.scss'

const ROUTES = {
  'prof-review': '/backoffice/skill-set/prof-review',
  'generate-course': '/backoffice/skill-set/generate-course',
  courses: '/backoffice/skill-set/courses',
}

const SUBTITLE = {
  'prof-review': 'Найдите профессию, соберите недостающие навыки — ИИ составит под них курс',
  'generate-course': 'Выберите навык — ИИ предложит до четырёх вариантов плана',
  'generate-course-doc': 'Загрузите программу или методичку — ИИ превратит её в план курса',
  courses: 'Курсы, созданные из ваших планов. Материалы дописываются в фоне',
}

function SkillSetContainer() {
  const variable_18 = variableLanguage({ keyPage: 'generateCourse', keyVariable: 'variable_18' })

  const { pathname } = useLocation()
  const history = useNavigate()
  const dispatch = useDispatch()
  const { user, config, showModalBuy, selectedTag, selectedDocument, language, loadingIndx } = useSelector(
    (state) => state.Reducer
  )
  const { list, remove, refresh } = useTrajectory()

  const activeKey =
    pathname === ROUTES['generate-course'] ? 'generate-course' : pathname === ROUTES.courses ? 'courses' : 'prof-review'
  const [compact, setCompact] = useState(false)
  // высота полосы плана: на неё статичная зона уезжает вверх, оставляя строку шагов
  const planRef = useRef(null)
  // высота неподвижной верхней зоны шага 1 → --gen-list-h: под ней списки профессий и вакансий
  // получают свою высоту и прокрутку
  const rootRef = useRef(null)
  useEffect(() => {
    const root = rootRef.current
    const zone = root && root.querySelector('[data-gen-topzone]')
    if (!zone || typeof ResizeObserver === 'undefined') return
    const update = () => {
      const top = getComputedStyle(zone).position === 'sticky' ? zone.offsetHeight : 0
      root.style.setProperty('--gen-list-h', `${Math.max(360, window.innerHeight - top - 60)}px`)
    }
    const ro = new ResizeObserver(update)
    ro.observe(zone)
    window.addEventListener('resize', update)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [activeKey])
  // источник для шага 2: навык из учебного плана или загруженный документ
  const [source, setSourceState] = useState('skill')
  const byDoc = activeKey === 'generate-course' && source === 'doc'
  const setSource = (value) => {
    setSourceState(value)
    if (value === 'doc') dispatch(setSelectedTag(''))
    else dispatch(setSelectedDocument(null))
  }

  useEffect(() => {
    const el = document.getElementById('element_for_scroll_up')
    if (!el) return
    el.scrollTop = 0
    // только тень у прилипшей строки шагов — без изменения высоты, чтобы прокрутка не дёргалась
    const onScroll = () => setCompact(el.scrollTop > 60)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    refresh()
    requests.users
      .get_config()
      .then((res) => dispatch(setConfig(res.data)))
      .catch((err) => console.error(err))
  }, [])

  const go = (key) => history(ROUTES[key])

  const selectSkill = (skill) => {
    if (loadingIndx !== null && loadingIndx !== undefined) return message.warning(variable_18)
    dispatch(setSelectedTag(skill))
  }

  const firstWithoutCourse = list.find((x) => skillStatus(x).kind !== 'course')
  const generateFor = (skill) => {
    if (skill) dispatch(setSelectedTag(skill))
    go('generate-course')
  }

  const limit = user?.education_plan_limit || 0
  const withoutCourse = list.filter((x) => skillStatus(x).kind !== 'course').length

  let planMeta = pluralN(list.length, ['навык', 'навыка', 'навыков'])
  if (activeKey === 'generate-course') planMeta = 'Выберите навык для курса'
  if (activeKey === 'courses')
    planMeta = withoutCourse
      ? `${pluralN(withoutCourse, ['навык', 'навыка', 'навыков'])} ещё без курса`
      : 'У всех навыков есть курс'

  return (
    <div className={s.root} ref={rootRef}>
      <div className={s.top}>
        <div>
          <h1>Генератор курсов и тестов</h1>
          <div className={s.muted}>{SUBTITLE[byDoc ? 'generate-course-doc' : activeKey]}</div>
        </div>
        <div className={s.bal}>
          <span className={cn(s.muted, s.small)}>Осталось</span>
          <b>{limit}</b>
          <span className={s.small}>{plural(limit, ['генерация', 'генерации', 'генераций'])}</span>
          <button type="button" className={cn(s.btn, s.sec, s.sm)} onClick={() => dispatch(setShowBuyModal(true))}>
            Пополнить
          </button>
        </div>
      </div>

      {/* строка шагов над учебным планом и прилипает при прокрутке; план уезжает вместе со страницей.
          На шаге 1 неподвижна вся верхняя зона (шаги + план + зарплаты) — прокручиваются только
          профессии и вакансии. На остальных шагах обёртка display: contents и не влияет на раскладку. */}
      <div
        className={cn(s.topZone, { [s.topZoneFixed]: activeKey === 'prof-review', [s.compact]: compact })}
        data-gen-topzone=""
      >
        <div className={cn(s.stepsBar, { [s.compact]: compact && activeKey !== 'prof-review' })} data-gen-static="">
          <StepsNav active={activeKey} onGo={go} summary={stepSummary(list)} byDoc={byDoc} />
        </div>
        <div className={s.static}>
          <section
            ref={planRef}
            className={cn(s.planbar, 'gen-intro-plan', { [s.planbarSplit]: activeKey === 'prof-review' })}
            aria-label="Учебный план"
          >
            <div className={s.planLeft}>
              <div className={s.planHead}>
                <h3>{byDoc ? 'Документ-источник' : 'Учебный план'}</h3>
                <span className={cn(s.muted, s.small)}>
                  {byDoc ? 'ИИ возьмёт структуру и темы из файла' : planMeta}
                </span>
              </div>

              {byDoc ? (
                <DocSource
                  bare
                  selected={selectedDocument}
                  language={language || localStorage.getItem('language') || 'ru'}
                  onSelect={(doc) => dispatch(setSelectedDocument(doc))}
                />
              ) : activeKey === 'generate-course' ? (
                <ChipsBox count={list.length} role="radiogroup" aria-label="Навык для курса">
                  {list.map((skill) => {
                    const on = selectedTag?.name === skill.name
                    const st = skillStatus(skill)
                    return (
                      <button
                        type="button"
                        role="radio"
                        aria-checked={on}
                        key={skill.id || skill.name}
                        className={cn(s.pchip, s.pchipBtn, { [s.pchipOn]: on })}
                        onClick={() => selectSkill(skill)}
                      >
                        <span className={s.dot} style={{ background: on ? '#fff' : st.dot }} />
                        <span>{skill.name}</span>
                        <span className={cn(s.muted, s.small)}>{st.meta}</span>
                      </button>
                    )
                  })}
                  <button type="button" className={cn(s.pchip, s.pchipDashed)} onClick={() => go('prof-review')}>
                    + Навык из профессий
                  </button>
                </ChipsBox>
              ) : (
                <ChipsBox
                  count={list.length}
                  footer={
                    activeKey === 'prof-review' && (
                      <>
                        <button
                          type="button"
                          className={cn(s.btn, s.pri)}
                          disabled={!list.length}
                          onClick={() => generateFor(firstWithoutCourse || list[0])}
                        >
                          Сгенерировать →
                        </button>
                      </>
                    )
                  }
                >
                  {activeKey === 'prof-review' && <SkillAdd />}
                  {list.map((skill) => (
                    <PlanChip
                      key={skill.name}
                      skill={skill}
                      removable={activeKey === 'prof-review' && skillStatus(skill).kind !== 'course'}
                      plain={activeKey === 'prof-review'}
                      onRemove={() => remove(skill)}
                    />
                  ))}
                </ChipsBox>
              )}
              {activeKey === 'courses' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {firstWithoutCourse && (
                    <button type="button" className={cn(s.btn, s.sec)} onClick={() => generateFor(firstWithoutCourse)}>
                      Создать курс: «{firstWithoutCourse.name}» →
                    </button>
                  )}
                </div>
              )}
            </div>
            {activeKey === 'prof-review' && <SalaryPanel />}
          </section>
        </div>
      </div>

      <Outlet context={{ source, setSource }} />

      <ModalBuy
        openModalBuy={showModalBuy}
        setOpenModalBuy={(value) => dispatch(setShowBuyModal(value))}
        config={config}
      />
    </div>
  )
}

export default SkillSetContainer
